import { useEffect, useMemo, useState } from "react";
import { Lock, Coins, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Paid Live paywall — the server refused room access (402 paid_entry_required).
// A viewer unlocks by sending a QUALIFYING GIFT (>= the Live's coin setting) through
// the normal gift flow; access is granted only after that gift succeeds.
export default function PaidLivePaywall({ streamId, hostName, price, onUnlocked, onClose }) {
  const [status, setStatus] = useState(null);
  const [giftKey, setGiftKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const entryKey = useMemo(() => `${streamId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, [streamId]);
  const load = () => api.get(`/live/${streamId}/entry`).then(({ data }) => { setStatus(data); setGiftKey((k) => k || data?.qualifying_gifts?.[0]?.key || null); }).catch(() => setStatus({}));
  useEffect(() => { load(); }, [streamId]); // eslint-disable-line react-hooks/exhaustive-deps
  const gifts = status?.qualifying_gifts || [];
  const gift = gifts.find((g) => g.key === giftKey) || gifts[0];
  const balance = status?.balance ?? null;
  const short = balance != null && gift ? Math.max(0, gift.coins - balance) : 0;
  const reversed = status?.status === "refunded" || status?.status === "chargeback";
  const send = async () => {
    if (busy || !gift) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/live/${streamId}/entry/unlock`, { gift_key: gift.key, entry_key: entryKey });
      if (["paid", "exempt", "free"].includes(data?.status)) { toast.success(`${gift.emoji} ${gift.label} sent — you're in!`); onUnlocked?.(); }
      else toast.error("The gift did not go through.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : "Could not send the gift."));
      load();
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black flex items-center justify-center p-6" data-testid="paid-live-paywall">
      <div className="glass p-8 max-w-md w-full border border-white/10">
        <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-3 flex items-center gap-2"><Lock size={12} /> Paid Live</div>
        <h2 className="font-display text-3xl mb-2">{hostName ? `${hostName}'s Live is locked.` : "This Live is locked."}</h2>
        <p className="text-sm text-zinc-400 leading-relaxed mb-5">
          Send a gift worth <span className="text-white font-semibold" data-testid="paid-live-price">{Number(price).toLocaleString()} coins</span> or more to get in. The gift goes to {hostName || "the creator"} like any other gift — you&apos;re let in the moment it lands. Entry is for this Live only.
        </p>
        <div className="grid grid-cols-3 gap-2 mb-5" data-testid="paid-live-gift-picker">
          {gifts.slice(0, 6).map((g) => (
            <button key={g.key} type="button" onClick={() => setGiftKey(g.key)} data-testid={`paid-live-gift-${g.key}`}
              className={`rounded-md border px-2 py-3 text-center transition-colors ${gift?.key === g.key ? "border-yellow-400 bg-yellow-400/10" : "border-white/10 hover:border-white/30"}`}>
              <div className="text-2xl leading-none mb-1">{g.emoji}</div>
              <div className="text-[11px] text-white font-semibold truncate">{g.label}</div>
              <div className="text-[10px] text-zinc-400">{g.coins.toLocaleString()} coins</div>
            </button>
          ))}
          {status && gifts.length === 0 && <div className="col-span-3 text-xs text-zinc-500">No gift in the catalog reaches this Live&apos;s entry level.</div>}
        </div>
        <div className="flex items-center justify-between text-sm mb-5 border border-white/10 rounded-md px-3 py-2">
          <span className="text-zinc-400 flex items-center gap-1.5"><Coins size={14} className="text-yellow-400" /> Your coins</span>
          <span className="text-white font-semibold" data-testid="paid-live-balance">{balance == null ? "…" : balance.toLocaleString()}</span>
        </div>
        {reversed ? (
          <p className="text-sm text-rose-300 mb-4" data-testid="paid-live-reversed">Your entry gift to this Live was reversed ({status.status}). Contact the creator to re-enter.</p>
        ) : short > 0 ? (
          <p className="text-sm text-amber-300 mb-4" data-testid="paid-live-short">You need {short.toLocaleString()} more coins to send {gift?.label ? `a ${gift.label}` : "this gift"}.</p>
        ) : null}
        <div className="flex gap-3">
          <button type="button" onClick={onClose} data-testid="paid-live-leave"
            className="flex-1 border border-white/15 text-zinc-200 font-semibold px-5 py-3 rounded-full hover:bg-white/5">Not now</button>
          {!reversed && (
            <button type="button" onClick={send} disabled={busy || !gift || balance == null || short > 0} data-testid="paid-live-unlock"
              className="flex-1 bg-yellow-400 hover:bg-yellow-300 disabled:opacity-50 text-black font-bold px-5 py-3 rounded-full flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
              {short > 0 ? "Not enough coins" : gift ? `Send ${gift.emoji} ${gift.label} · ${gift.coins.toLocaleString()}` : "Send gift"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
